/**
 * The Operations promotions browser pool, sourced from the CRM Events `/campaigns`
 * endpoint (scoped to the active brand's tenant).
 *
 * Each item is mapped onto the browser-card `Promotion` by the shared
 * `api/campaign-map` mapper (also used by the promotion-detail hook, so the list
 * and the detail resolve an item identically). The mapping reads every field
 * defensively and never throws, so the browser shows its empty states when a
 * tenant has no operations data.
 */
import React from 'react';

import { useAppSelector } from '@/store';

import type { Promotion } from '../../mock-data/types';
import { listOperationsCampaigns } from './api/crm-events';
import { toPromotion } from './api/campaign-map';

export type OperationsPromotionsResult = { pool: Promotion[]; loading: boolean };

export const useOperationsPromotions = (): OperationsPromotionsResult => {
  const tenantId = useAppSelector((state) => state.casinoInstance.displayBrand?.tenant_id);
  const [pool, setPool] = React.useState<Promotion[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!tenantId) {
      setLoading(true);
      setPool([]);
      return;
    }
    const controller = new AbortController();
    const { signal } = controller;
    setLoading(true);
    listOperationsCampaigns({ tenantId }, signal)
      .then((response) => {
        if (!signal.aborted) {
          setPool(response.campaigns.items.map(toPromotion));
          setLoading(false);
        }
      })
      .catch(() => {
        if (!signal.aborted) {
          setPool([]);
          setLoading(false);
        }
      });
    return () => controller.abort();
  }, [tenantId]);

  return { pool, loading };
};

export default useOperationsPromotions;
